/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */
import { graphql, useStaticQuery } from 'gatsby';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import { getLocaleAndBasePath } from 'utils/i18n';

type TSEO = {
  description?: string;
  lang?: string;
  meta?: any[];
  title?: string;
  keywords?: string;
  canonical?: string;
  imageSrc?: string;
  link?: any[];
  location: any;
  multilingual: boolean;
  structuredData?: any[];
};

function SEO({
  description,
  lang,
  meta = [],
  title,
  keywords,
  canonical,
  imageSrc,
  link = [],
  location,
  multilingual,
  structuredData,
}: TSEO) {
  const { site, imgMeta } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          siteUrl
        }
      }
      imgMeta: file(relativePath: { eq: "meta-default.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, width: 1200)
        }
      }
    }
  `);

  const defaultImg = imgMeta.childImageSharp.gatsbyImageData.images.fallback.src;

  const metaDescription = description || site.siteMetadata.description;
  const metaTitle = title || site.siteMetadata.title;
  const image = imageSrc ? imageSrc : defaultImg;

  const { siteUrl } = site.siteMetadata;
  const { locale, basePath } = getLocaleAndBasePath(location.pathname);

  return (
    <Helmet
      htmlAttributes={{
        lang: lang || locale,
      }}
      title={metaTitle}
      titleTemplate={`%s`}
      link={[
        ...link,
        ...(multilingual
          ? [
              {
                rel: 'alternate',
                hrefLang: 'x-default',
                href: `${siteUrl}${basePath}`,
              },
              {
                rel: 'alternate',
                hrefLang: 'en',
                href: `${siteUrl}${basePath}`,
              },
              {
                rel: 'alternate',
                hrefLang: 'fr',
                href: `${siteUrl}/fr${basePath}`,
              },
            ]
          : []),
        ...(canonical
          ? [
              {
                rel: 'canonical',
                href: canonical,
                key: canonical,
              },
            ]
          : []),
      ]}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          name: `keywords`,
          content: keywords || '',
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: `og:image`,
          content: image,
          prefix: 'og: http://ogp.me/ns#',
        },
        {
          name: `twitter:card`,
          content: `summary_large_image`,
        },
        {
          name: `twitter:creator`,
          content: `KOHO`,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        { rel: 'alternate', hreflang: 'x-default', href: '/' },
        // Pinterest domain verification meta tag (added at Ayaz's request)
        {
          name: 'p:domain_verify',
          content: 'eec529375e429e872a0db96b6b05baad',
        },
        {
          name: `twitter:image`,
          // TODO: Need a proper way to handle this URL
          content: image,
        },
        ...(!canonical || process.env.GATSBY_PUBLIC_SANITY_DATASET !== 'production'
          ? [{ name: 'robots', content: 'noindex' }]
          : []),
        // {
        //   name: `apple-itunes-app`,
        //   content: 'app-id=1091010942',
        // },
        // {
        //   name: `google-play-app`,
        //   content: 'app-id=ca.koho',
        // },
      ].concat(meta)}
    >
      {structuredData &&
        structuredData.map((schema, i) => (
          <script type="application/ld+json" key={i}>
            {JSON.stringify(schema)}
          </script>
        ))}
    </Helmet>
  );
}

SEO.defaultProps = {
  meta: [],
  description: ``,
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
};

export default SEO;
